import React, {
  ReactNode,
  RefObject,
  useEffect,
  useRef,
  useState,
  KeyboardEvent,
  useCallback,
} from 'react';
import compact from 'lodash/compact';
import clsx from 'clsx';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import {
  ToDo,
} from 'types/toDo';
import {
  useMedia,
} from 'core/hooks';
import {
  MediaOriginatorType,
} from 'pages/Dashboard/utils/constants';
import ChevronDownIcon from 'core/icons/chevronDown';
import AutoCompletedInput from 'core/components/AutoComplete';
import AbandonButton from 'pages/Dashboard/pages/Todos/components/AbandonButton';
import Urgent from 'pages/Dashboard/pages/Todos/components/Urgent';
import {
  useDetailedToDo,
  useToDoSettings,
  useUpdateToDoHandler,
} from 'pages/Dashboard/pages/Todos/hooks';
import {
  toDosEventChannel,
  visitNoteFormEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import Button from 'pages/Dashboard/components/Button';
import Comments from 'pages/Dashboard/pages/Todos/Comments';
import ApplicableStatusSelect from 'pages/Dashboard/pages/Todos/components/ApplicableStatusSelect';
import EmptyState from 'pages/Dashboard/pages/Todos/EmptyState';
import Name from 'pages/Dashboard/components/Name';
import isNil from 'lodash/isNil';
import {
  SidePanelComponentProps,
} from 'pages/Dashboard/contexts/sidePanel';
import FaxMultiMediaLink from 'pages/Dashboard/pages/Todos/FaxMultiMediaLink';
import useSignalREffect from 'views/EMR/hooks/signalR';
import Skeleton from '@mui/material/Skeleton';
import Editable from 'core/components/Editable';
import Input from 'pages/Dashboard/components/Input';
import {
  notEmptyString,
} from '@populateui/utils';
import {
  FormWorkflowStatus,
} from 'dtos/formWorkflowStatus';
import Tooltip from 'core/components/Tooltip';
import {
  getPatientChartPath,
} from 'pages/Dashboard/pages/Charts/utils';
import {
  getDisplayName,
} from 'pages/Dashboard/utils/helper';
import EditableCollaboratorsSummary from 'pages/Dashboard/pages/Todos/components/EditableCollaboratorsSummary';
import EditableAssignee from 'pages/Dashboard/pages/Todos/components/EditableAssignee';
import EditableDueDate from 'pages/Dashboard/pages/Todos/components/EditableDueDate';
import {
  getAssignedFormsDisabledTooltip,
} from 'views/EMR/components/FormWorkflow/utils';

type Parts = 'root' | 'title' | 'skeleton';

export type RowProps = {
  title: ReactNode | string;
  className?: string;
  children: ReactNode | string;
  delimiter?: string | null;
  classes?: Partial<Record<Parts, string>>;
  isLoading?: boolean;
};

function Row({
  title,
  className,
  classes,
  delimiter = '',
  children,
  isLoading,
}: RowProps) {
  return (
    <div className={clsx(
      'populate-information-row flex py-2 text-xs border-0 border-b-gray-300 items-center last:border-b-0',
      classes?.root,
      className,
    )}
    >
      <p className={clsx('text-gray-700 font-medium w-32', classes?.title)}>
        {title}
        {delimiter}
      </p>
      {isLoading ? (
        <Skeleton className={clsx('!h-4', classes?.skeleton)} />
      ) : (
        children
      )}
    </div>
  );
}

type EditableProps = {
  toDo: ToDo;
  handleChange: (patchData: Record<string, any>) => void;
  isUpdating: boolean;
};

function EditableToDoName({
  toDo,
  handleChange,
  isUpdating,
}: EditableProps) {
  const toDoNameInputRef = useRef<HTMLInputElement>(null);

  const [value, setValue] = useState<string>(toDo?.name ?? '');
  useEffect(() => {
    setValue(toDo?.name ?? '');
  }, [toDo?.name]);

  const onUpdate = useCallback(() => {
    if (value !== toDo?.name && notEmptyString(value)) {
      handleChange({ toDoName: value });
    }
    return true;
  }, [value, toDo?.name]);

  const onCancel = useCallback(() => {
    setValue(toDo?.name ?? '');
  }, [toDo?.name]);

  const nameRef = useRef<HTMLHeadingElement>(null);

  return (
    <Editable
      readonly={toDo?.isAbandoned || isUpdating}
      idleView={(
        <Tooltip title={value} ref={nameRef} auto>
          <h1 ref={nameRef} className="font-semibold text-xl flex-auto truncate break-all max-w-[400px]">
            {value}
          </h1>
        </Tooltip>
        )}
      classes={{ idleRoot: '!h-9' }}
      showActions={false}
      isLoading={isUpdating}
      controlRef={toDoNameInputRef as RefObject<HTMLElement>}
      onSubmit={onUpdate}
      onCancel={onCancel}
      tooltip="Update To-do Name"
    >
      {(onSubmit, onCancel) => (
        <Input
          value={value}
          id="to-do-name"
          className="w-full bg-white min-w-[25rem] !h-9"
          inputRef={toDoNameInputRef}
          showErrorText={false}
          onBlur={onSubmit}
          inputProps={{
            maxLength: 64,
          }}
          onChange={(updatedVal) => {
            setValue(updatedVal);
          }}
          onKeyDown={(e: KeyboardEvent<HTMLElement>) => {
            e.stopPropagation();
            if (e.key === 'Escape') {
              onCancel();
            } else if (e.key === 'Enter') {
              onSubmit();
            }
          }}
        />
      )}
    </Editable>
  );
}

export type TodoPanelInfo = { todoId: number };

export default function DetailedView({
  onClose,
  entity: { todoId: id },
  slots,
}: SidePanelComponentProps<TodoPanelInfo>) {
  const {
    data: toDo,
    isLoading,
    refetch,
  } = useDetailedToDo(id);
  const { getMediaPathById, requestMedia } = useMedia();
  const {
    asyncHandler: updateToDo,
    isLoading: isUpdating,
  } = useUpdateToDoHandler({ successMessage: 'To-do was updated successfully!' });

  useEffect(() => {
    const userIds = compact([
      toDo?.assignee?.userId,
      toDo?.creator?.userId,
    ]);
    if (userIds.length > 0) {
      requestMedia(userIds, MediaOriginatorType.User);
    }
  }, [toDo?.assignee?.userId, toDo?.creator?.userId, requestMedia]);

  const { categories: validCategories } = useToDoSettings();

  useEffect(() => {
    const unsubscribeFromChanges = toDosEventChannel.on('onToDosChanged', (toDoId) => {
      if (toDoId === id) {
        refetch();
      }
    });

    return () => { unsubscribeFromChanges(); };
  }, [id]);

  useSignalREffect('OnToDoUpdated', (todoId: number) => {
    toDosEventChannel.emit('onToDosChanged', todoId);
  }, []);

  const handleChange = async (patchData: Record<string, any>) => {
    await updateToDo({
      toDoId: id,
      dueDate: patchData.dueDate ?? toDo?.dueDate ?? null,
      ...patchData,
    });
  };

  const { faxId } = toDo?.faxToDo ?? {};

  const formWorkflow = toDo?.formWorkflow;
  const canOpenAssignedForms = isNil(formWorkflow)
    || formWorkflow.status === FormWorkflowStatus.AwaitingClinician;
  const assignedFormsDisabledTooltip = getAssignedFormsDisabledTooltip(formWorkflow?.status);

  return (isLoading && isNil(toDo)
    ? (
      <EmptyState
        isLoading={isLoading}
        loadingText="Loading To-do..."
      />
    ) : (
      <div className="flex flex-col w-full bg-white rounded-xl justify-between h-full pb-7 !max-w-[640px] !min-h-[840px]">
        <div className="flex justify-between flex-col h-full">
          <div className="w-full">
            <div className="flex flex-col mb-1 py-4 px-6 border-b border-gray-200">
              <div className="flex flex-row items-start justify-between w-full">
              <div className="flex w-full items-center gap-4">
                {slots?.header}
                <Urgent
                  {...(toDo as ToDo)}
                  classes={{
                    icon: clsx('!w-6 !h-6', {
                      '!text-gray-600': !toDo?.isUrgent,
                    }),
                    root: clsx('border rounded', {
                      'bg-gray-100 border-gray-100': !toDo?.isUrgent,
                      'bg-red-100 border-red-100': toDo?.isUrgent,
                    }),
                  }}
                />
                <div className="flex flex-col gap-1">
                  <EditableToDoName
                    toDo={toDo as ToDo}
                    handleChange={handleChange}
                    isUpdating={isUpdating}
                  />
                  <div className="!text-xs pl-1 flex items-center gap-1">
                    Created on
                    <span className="font-medium">{toDo?.formattedDate}</span>
                    by
                    <Name
                      {...(toDo?.creator?.formattedNames ?? {})}
                      image={{
                        size: 'xs',
                        src: getMediaPathById(toDo?.creator?.userId ?? 0, MediaOriginatorType.User) ?? undefined,
                      }}
                      name={getDisplayName(toDo?.creator?.formattedNames)}
                      className="!w-[initial] font-medium !gap-1 pl-1"
                    />
                </div>
                </div>
              </div>
              <div className="flex-1 flex items-center justify-end text-right -mr-2">
                {!toDo?.isAbandoned && (
                  <AbandonButton toDo={toDo} />
                )}
                {slots?.actions}
                <IconButton onClick={onClose}>
                  <CloseIcon className="!text-xl" />
                </IconButton>
              </div>
              </div>
            </div>
            <div className="px-6 pb-2 flex flex-col border-b border-gray-200">
              <Row title="Assigned to" className="!border-gray-100 !h-12">
                <EditableAssignee
                  toDo={toDo as ToDo}
                  handleChange={handleChange}
                  isUpdating={isUpdating}
                />
              </Row>
              <Row title="Collaborator" className="!border-gray-100">
                <EditableCollaboratorsSummary
                  toDo={toDo as ToDo}
                />
              </Row>
              <Row title="Patient" className="!border-gray-100">
                <Name
                  className="!text-xs !w-[initial]"
                  link={getPatientChartPath(toDo?.patient?.patientId)}
                  name={getDisplayName(toDo?.patient?.formattedNames)}
                  image={{ src: getMediaPathById(toDo?.patientId ?? 0) ?? undefined }}
                />
              </Row>
              <Row title="Category" className="!border-gray-100">
                <AutoCompletedInput<string>
                  id="to-do-category"
                  className={clsx('!w-auto max-w-[144px] bg-white !h-6', {
                    'w-36': !toDo?.isAbandoned,
                  })}
                  popupIcon={<ChevronDownIcon className="search-icon !text-xl" />}
                  classes={{
                    inputRoot: '!py-1 !px-2 !min-w-36',
                    input: '!w-6 !inline-block',
                  }}
                  openOnFocus
                  options={validCategories}
                  getLabel={(val) => val}
                  disabled={isUpdating}
                  onChange={(val: string | string[]) => {
                    handleChange({ category: val });
                  }}
                  readonly={toDo?.isAbandoned}
                  value={toDo?.category}
                />
              </Row>
              <Row title="Status" className="!border-gray-100">
                <ApplicableStatusSelect
                  className="mr-2"
                  toDoId={toDo?.toDoId ?? 0}
                  dueDate={toDo?.dueDate ?? null}
                  value={toDo?.currentStatus}
                  isAbandoned={toDo?.isAbandoned}
                  options={toDo?.availableStatuses ?? []}
                />
              </Row>
              <Row title="Due date" className="!border-gray-100 !h-12">
                <EditableDueDate
                  toDo={toDo as ToDo}
                  className="flex items-center py-1"
                  classes={{
                    value: '!text-xs !leading-5',
                    emptyValue: '!text-xs !leading-5',
                  }}
                  displayVariant="text"
                />
              </Row>
              {!isNil(faxId) && (
                <Row title="Fax" className="!border-gray-100">
                  <FaxMultiMediaLink faxId={faxId} />
                </Row>
              )}
              {!isNil(formWorkflow) ? (
                <Row title="Forms" className="!border-gray-100">
                  <Tooltip
                    title={assignedFormsDisabledTooltip}
                    hide={canOpenAssignedForms}
                  >
                    <span className={clsx({ 'inline-block cursor-not-allowed': !canOpenAssignedForms })}>
                      <Button
                        variant="contained"
                        color="primary"
                        className="!normal-case"
                        disabled={!canOpenAssignedForms}
                        onClick={() => {
                          visitNoteFormEventChannel.emit('onOpenClinicianFormStep', {
                            formWorkflowId: formWorkflow.formWorkflowId!,
                            category: formWorkflow.category!,
                            patient: toDo?.patient,
                          });
                        }}
                      >
                        Open assigned forms
                      </Button>
                    </span>
                  </Tooltip>
                </Row>
              ) : null}
            </div>
          </div>
          <Comments
            toDoId={toDo?.toDoId ?? 0}
            hasComments={toDo?.hasComments}
            isAbandoned={toDo?.isAbandoned}
          />
        </div>
      </div>
    )
  );
}
